/**
 * The single source of truth for all application routes.
 *
 * Adding a new page:
 *   1. Lazy-import the page component below.
 *   2. Append a new `RouteModule` entry to `ROUTES`.
 *   3. (Optional) gate it behind a feature flag with `feature: '...'`.
 *   4. (Optional) add a sidebar entry with `nav: { ... }`.
 *   That is *all*. App.tsx and the sidebar pick up the change automatically,
 *   the per-page error boundary is wired in, role guards are enforced.
 *
 * Removing a page:
 *   Delete the entry. Routes, nav and access control update automatically.
 */

import { lazy } from 'react';
import {
  Home,
  LayoutDashboard,
  Users,
  Calendar,
  Mic,
  Receipt,
  Video,
  MessageSquare,
  UtensilsCrossed,
  BarChart2,
  Clock,
  Hospital,
  UsersRound,
  UserCheck,
  Inbox,
  ClipboardList,
  ChefHat,
  TrendingUp,
  Sparkles,
  Stethoscope,
  Shield,
  Settings as SettingsIcon,
  Building2,
  Database,
  BookOpen,
  LayoutGrid,
} from 'lucide-react';

import Login from '@/pages/Login';
import Landing from '@/pages/Landing';
import LandingPraxis from '@/pages/LandingPraxis';
import LandingKlinik from '@/pages/LandingKlinik';
import DemoLanding from '@/pages/DemoLanding';

import type { Role, RouteModule } from './types';

// ─── Rollen-Buckets ──────────────────────────────────────────────────────
//
// `admin` (Plattform-Admin) erscheint hier bewusst NICHT: Plattform-Admins
// sehen ausschließlich die Routen unter `/admin/*`. Wer eine Kunden-Org
// einsehen muss, startet eine Support-Session — siehe `SupportModeContext`.

/** Alle Rollen, die in einer Kunden-Org (Klinik oder Praxis) arbeiten. */
const WORKFLOW_ROLES: readonly Role[] = [
  'therapeut',
  'assistenz',
  'org_admin',
] as const;

// ─── Lazy-loaded pages ───────────────────────────────────────────────────
//
// Eagerly imported pages live above (Login, Landing, NotFound) because they
// are reached during the very first paint. Everything else is lazy-loaded so
// the initial JS bundle stays small.
//
// One declaration per page; alphabetised within each section so collisions
// are obvious.

// Authenticated app pages
const Dashboard          = lazy(() => import('@/pages/Dashboard'));
const Patients           = lazy(() => import('@/pages/Patients'));
const PatientDetail      = lazy(() => import('@/pages/PatientDetail'));
const CalendarPage       = lazy(() => import('@/pages/CalendarPage'));
const SessionRecording   = lazy(() => import('@/pages/SessionRecording'));
const Settings           = lazy(() => import('@/pages/Settings'));
const Invoices           = lazy(() => import('@/pages/Invoices'));
const Telemedicine       = lazy(() => import('@/pages/Telemedicine'));
const Messages           = lazy(() => import('@/pages/Messages'));
const NutritionPlans     = lazy(() => import('@/pages/NutritionPlans'));
const Reporting          = lazy(() => import('@/pages/Reporting'));
const Warteliste         = lazy(() => import('@/pages/Warteliste'));
const Gruppen            = lazy(() => import('@/pages/Gruppen'));
const Screenings         = lazy(() => import('@/pages/Screenings'));
const Kundenverwaltung   = lazy(() => import('@/pages/Kundenverwaltung'));
const TherapeutenUebersicht = lazy(() => import('@/pages/TherapeutenUebersicht'));
const KiPosteingang      = lazy(() => import('@/pages/KiPosteingang'));
const InboxPage          = lazy(() => import('@/pages/Inbox'));
const ProgrammePage      = lazy(() => import('@/pages/ProgrammePage'));
const UebergabenPage     = lazy(() => import('@/pages/UebergabenPage'));
const PraxisAdmin        = lazy(() => import('@/pages/PraxisAdmin'));

// Magic AI Importer (Phase 2)
const MagicImporter      = lazy(() => import('@/pages/MagicImporter'));
const MagicImporterReview = lazy(() => import('@/pages/MagicImporterReview'));

// Flow-Redesign (Beta) — parallel task-focused UI behind `ui_flow_mode`.
const HeuteScreen        = lazy(() => import('@/features/flow/HeuteScreen'));
const ConsultationScreen = lazy(() => import('@/features/flow/ConsultationScreen'));
const ReviewScreen       = lazy(() => import('@/features/flow/ReviewScreen'));

// Admin
const AuditLog           = lazy(() => import('@/pages/admin/AuditLog'));
const BlsImport          = lazy(() => import('@/pages/admin/BlsImport'));
const LeitlinienImport   = lazy(() => import('@/pages/admin/LeitlinienImport'));
const PricingManager     = lazy(() => import('@/pages/admin/PricingManager'));
const BreachReport       = lazy(() => import('@/pages/admin/BreachReport'));

// Clinic Admin (org_admin role, scope: 'klinik')
const ClinicAdmin        = lazy(() => import('@/pages/ClinicAdmin'));
const StationsUebersicht = lazy(() => import('@/pages/StationsUebersichtPage'));
const KonsilInbox        = lazy(() => import('@/pages/KonsilInboxPage'));
const KuechenExport      = lazy(() => import('@/pages/KuechenExportPage'));
const KpiDashboard       = lazy(() => import('@/pages/klinik/KpiDashboardPage'));
const ScreeningList      = lazy(() => import('@/pages/klinik/ScreeningListPage'));

// NutriKnows knowledge module
const NutriKnowsHome     = lazy(() => import('@/nutriknows/pages/NutriKnowsHome'));
const NutriKnowsHistory  = lazy(() => import('@/nutriknows/pages/NutriKnowsHistory'));
const NutriKnowsSettings = lazy(() => import('@/nutriknows/pages/NutriKnowsSettings'));
const NutriKnowsDemo     = lazy(() => import('@/nutriknows/pages/NutriKnowsDemo'));

// Phase 5 — Module Browser
const ModuleBrowser      = lazy(() => import('@/pages/ModuleBrowser'));

// Public / patient-facing pages
const PatientPortal      = lazy(() => import('@/pages/PatientPortal'));
const PatientInvite      = lazy(() => import('@/pages/PatientInvite'));
const BookingPage        = lazy(() => import('@/pages/BookingPage'));
const IntakePage         = lazy(() => import('@/pages/IntakePage'));
const Pricing            = lazy(() => import('@/pages/Pricing'));
const Impressum          = lazy(() => import('@/pages/Impressum'));
const Datenschutz        = lazy(() => import('@/pages/Datenschutz'));
const AGB                = lazy(() => import('@/pages/AGB'));

/**
 * The full route table. Order in this list controls *only* the order of
 * declarations — sidebar order is governed by `nav.order`.
 */
export const ROUTES: readonly RouteModule[] = [
  // ── Public, no Layout ─────────────────────────────────────────────────
  // Workspace-spezifische Login-URLs (maximale Trennung). Dieselbe Page-
  // Komponente; der konkrete Workspace ergibt sich aus dem Pfad und
  // entscheidet, welches Sub-Form gerendert wird und in welchen Bereich
  // der User nach Login geht. `/login` zeigt den Workspace-Picker.
  { id: 'login',          path: '/login',          component: Login,    kind: 'public' },
  { id: 'login-admin',    path: '/login/admin',    component: Login,    kind: 'public' },
  { id: 'login-klinik',   path: '/login/klinik',   component: Login,    kind: 'public' },
  { id: 'login-ambulant', path: '/login/ambulant', component: Login,    kind: 'public' },
  { id: 'login-patient',  path: '/login/patient',  component: Login,    kind: 'public' },
  { id: 'landing',       path: '/',               component: Landing,  kind: 'public' },
  { id: 'landing-praxis', path: '/praxis',        component: LandingPraxis,
    kind: 'public', errorContext: 'Landingpage Praxis' },
  { id: 'landing-klinik', path: '/klinik',        component: LandingKlinik,
    kind: 'public', errorContext: 'Landingpage Klinik' },
  { id: 'demo-landing',   path: '/demo',          component: DemoLanding,
    kind: 'public', errorContext: 'Demo' },
  { id: 'nutriknows-demo', path: '/nutriknows-demo', component: NutriKnowsDemo,
    kind: 'public', featureFlag: 'nutriknows_rag', errorContext: 'NutriKnows-Demo' },
  { id: 'pricing',       path: '/pricing',        component: Pricing,
    kind: 'public', noErrorBoundary: true },
  { id: 'impressum',     path: '/impressum',      component: Impressum,
    kind: 'public', noErrorBoundary: true },
  { id: 'datenschutz',   path: '/datenschutz',    component: Datenschutz,
    kind: 'public', noErrorBoundary: true },
  { id: 'agb',           path: '/agb',            component: AGB,
    kind: 'public', noErrorBoundary: true },
  { id: 'patient-portal',     path: '/portal',
    component: PatientPortal, kind: 'public', featureFlag: 'patient_portal', errorContext: 'Patientenportal' },
  { id: 'patient-invite',     path: '/portal/invite/:code',
    component: PatientInvite, kind: 'public', featureFlag: 'patient_portal', errorContext: 'Einladung' },
  { id: 'booking-page',       path: '/booking/:orgId',
    component: BookingPage,   kind: 'public', featureFlag: 'booking_page', errorContext: 'Buchung' },
  { id: 'intake-page',        path: '/intake/:token',
    component: IntakePage,    kind: 'public', featureFlag: 'intake_page', errorContext: 'Anamnese' },

  // ── Always-on authenticated pages ─────────────────────────────────────
  {
    id: 'dashboard',
    path: '/dashboard',
    component: Dashboard,
    featureFlag: 'dashboard_simple',
    errorContext: 'Dashboard',
    roles: [...WORKFLOW_ROLES],
    nav: {
      icon: Home, label: 'Dashboard', order: 10,
      tabBarPriority: 1,
      tabBarDefault: ['praxis', 'klinik'],
    },
  },
  {
    // Phase 3 — Unified Review-Cockpit (TRIAGE). Ersetzte den alten
    // generischen "Posteingang"-Sidebar-Eintrag (`/posteingang` →
    // REDIRECTS auf `/inbox`). NICHT identisch mit `/ki-posteingang`:
    // das ist die eigenständige CAPTURE-Seite (siehe `ki-posteingang`
    // weiter unten) und wird NICHT hierher umgeleitet.
    id: 'inbox',
    path: '/inbox',
    component: InboxPage,
    featureFlag: 'clinical_consults',
    errorContext: 'Review-Cockpit',
    roles: [...WORKFLOW_ROLES],
    nav: { icon: Inbox, label: 'Review-Cockpit', order: 12 },
  },
  {
    // KiPosteingang ist die live CAPTURE-Seite: Freitext/Datei →
    // KI-Routing → strukturierte Speicherung. Eine eigenständige
    // Funktion, NICHT ein Redirect auf das `/inbox`-Triage-Cockpit.
    // Verlinkt u.a. aus dem QuickEntryWidget (Dashboard). Die Sidebar
    // zeigt diese Route nicht (kein `nav`), Einstieg läuft über die
    // Capture-Widgets.
    id: 'ki-posteingang',
    path: '/ki-posteingang',
    component: KiPosteingang,
    featureFlag: 'clinical_consults',
    errorContext: 'KI-Posteingang',
    roles: [...WORKFLOW_ROLES],
  },
  {
    id: 'nutriknows-home',
    path: '/nutriknows',
    component: NutriKnowsHome,
    featureFlag: 'nutriknows_rag',
    errorContext: 'NutriKnows',
    roles: [...WORKFLOW_ROLES],
    nav: { icon: Sparkles, label: 'NutriKnows', order: 30 },
  },
  {
    id: 'nutriknows-history',
    path: '/nutriknows/history',
    component: NutriKnowsHistory,
    featureFlag: 'nutriknows_rag',
    errorContext: 'NutriKnows-Historie',
    roles: [...WORKFLOW_ROLES],
  },
  {
    id: 'nutriknows-settings',
    path: '/nutriknows/settings',
    component: NutriKnowsSettings,
    featureFlag: 'nutriknows_rag',
    errorContext: 'NutriKnows-Einstellungen',
    roles: [...WORKFLOW_ROLES],
  },
  {
    id: 'patients',
    path: '/patients',
    component: Patients,
    featureFlag: 'patients_core',
    errorContext: 'Patientenliste',
    roles: [...WORKFLOW_ROLES],
    nav: {
      icon: Users, label: 'Patienten', order: 40,
      tabBarPriority: 2,
      tabBarDefault: ['praxis'],
    },
  },
  {
    id: 'patient-detail',
    path: '/patients/:id',
    component: PatientDetail,
    featureFlag: 'patients_core',
    errorContext: 'Patientenakte',
    roles: [...WORKFLOW_ROLES],
  },
  {
    id: 'calendar',
    path: '/calendar',
    component: CalendarPage,
    featureFlag: 'calendar_basic',
    errorContext: 'Kalender',
    roles: [...WORKFLOW_ROLES],
    nav: {
      icon: Calendar, label: 'Kalender', order: 50,
      tabBarPriority: 4,
      tabBarDefault: ['praxis'],
    },
  },
  {
    id: 'screenings',
    path: '/screenings',
    component: Screenings,
    featureFlag: 'screening_tools',
    errorContext: 'Screenings',
    roles: [...WORKFLOW_ROLES],
    nav: { icon: Stethoscope, label: 'Screenings', order: 145 },
  },
  {
    id: 'stationsuebersicht',
    path: '/klinik/stationen',
    component: StationsUebersicht,
    featureFlag: 'station_overview',
    errorContext: 'Stationsübersicht',
    scope: 'klinik',
    feature: 'klinik_stationsuebersicht',
    roles: [...WORKFLOW_ROLES],
    // Primary nav slot for klinik orgs; praxis orgs never see this
    // entry because `scope: 'klinik'` hides it via the sidebar filter.
    nav: {
      icon: Hospital,
      label: 'Stationen',
      order: 55,
      visibility: 'primary',
      tabBarPriority: 2,
      tabBarDefault: ['klinik'],
    },
  },
  {
    id: 'konsil-inbox',
    path: '/klinik/konsile',
    component: KonsilInbox,
    featureFlag: 'clinical_consults',
    errorContext: 'Konsil-Inbox',
    scope: 'klinik',
    feature: 'klinik_konsile',
    roles: [...WORKFLOW_ROLES],
    nav: {
      icon: ClipboardList,
      label: 'Konsile',
      order: 57,
      visibility: 'primary',
    },
  },
  {
    id: 'kuechen-export',
    path: '/klinik/kuechen-export',
    component: KuechenExport,
    featureFlag: 'diet_orders',
    errorContext: 'Küchen-Export',
    scope: 'klinik',
    feature: 'klinik_kuechen_export',
    roles: [...WORKFLOW_ROLES],
    nav: {
      icon: ChefHat,
      label: 'Küchen-Export',
      order: 59,
      visibility: 'secondary',
    },
  },
  {
    id: 'klinik-nutriscreen',
    path: '/klinik/screenings',
    component: ScreeningList,
    featureFlag: 'nutriscreen',
    errorContext: 'NutriScreen-Liste',
    scope: 'klinik',
    feature: 'klinik_nutriscreen',
    roles: [...WORKFLOW_ROLES],
    nav: {
      icon: Stethoscope,
      label: 'NutriScreen',
      order: 58,
      visibility: 'primary',
      tabBarPriority: 4,
      tabBarDefault: ['klinik'],
    },
  },
  {
    id: 'kpi-dashboard',
    path: '/klinik/kpi',
    component: KpiDashboard,
    featureFlag: 'station_overview',
    errorContext: 'KPI-Dashboard',
    scope: 'klinik',
    feature: 'klinik_kpi_dashboard',
    roles: [...WORKFLOW_ROLES],
    nav: {
      icon: TrendingUp,
      label: 'KPI §110a',
      order: 61,
      visibility: 'primary',
    },
  },
  {
    id: 'reporting',
    path: '/reporting',
    component: Reporting,
    featureFlag: 'reporting_analytics',
    errorContext: 'Reporting',
    roles: [...WORKFLOW_ROLES],
    // Renamed from "Berichte" in PR 1.2: "Berichte" also means GNCP-/Therapie-
    // reports in the clinical workflow, which was a constant source of
    // confusion. "Auswertungen" makes it unambiguous that this page is about
    // KPIs and statistics, not patient letters.
    nav: { icon: BarChart2, label: 'Auswertungen', order: 200 },
  },
  {
    id: 'messages',
    path: '/messages',
    component: Messages,
    featureFlag: 'messaging',
    errorContext: 'Nachrichten',
    roles: [...WORKFLOW_ROLES],
    nav: {
      icon: MessageSquare, label: 'Nachrichten', order: 999,
      badgeKey: 'unread-messages',
      tabBarPriority: 3,
      tabBarDefault: ['praxis', 'klinik'],
    },
  },

  // ── Feature-gated pages (sidebar entry hidden when disabled) ──────────
  // ─── Praxis-only ────────────────────────────────────────────────────
  // `scope: 'praxis'` mirrors the existing `scope: 'klinik'` tagging on
  // Stations / Konsile / Küchen-Export so the registry is symmetric:
  // klinik orgs no longer see ambulatory-only routes (Rechnungen, GOÄ-
  // Telemedizin, Warteliste, Gruppentherapie). Multi-Therapeuten is also
  // praxis-only because clinic teams are managed via `clinic-admin` →
  // Settings > Team. Superadmins keep cross-mode access (ADR 0007).
  {
    id: 'invoices',
    path: '/invoices',
    component: Invoices,
    featureFlag: 'billing_module',
    feature: 'rechnungen',
    errorContext: 'Rechnungen',
    scope: 'praxis',
    roles: [...WORKFLOW_ROLES],
    nav: { icon: Receipt, label: 'Rechnungen', order: 100 },
  },
  {
    id: 'nutrition-plans',
    path: '/nutrition-plans',
    component: NutritionPlans,
    featureFlag: 'diet_orders',
    feature: 'ernaehrungsplaene',
    errorContext: 'Ernährungspläne',
    scope: 'praxis',
    roles: [...WORKFLOW_ROLES],
    nav: { icon: UtensilsCrossed, label: 'Ernährungspläne', order: 110 },
  },
  {
    id: 'session',
    path: '/session',
    component: SessionRecording,
    featureFlag: 'session_recording_standalone',
    feature: 'sitzungsaufnahme',
    errorContext: 'Sitzungsaufnahme',
    scope: 'praxis',
    roles: [...WORKFLOW_ROLES],
    nav: { icon: Mic, label: 'Sitzungsaufnahme', order: 120 },
  },
  {
    id: 'telemedicine',
    path: '/telemedicine',
    component: Telemedicine,
    featureFlag: 'telemedizin',
    feature: 'telemedizin',
    errorContext: 'Telemedizin',
    scope: 'praxis',
    roles: [...WORKFLOW_ROLES],
    nav: { icon: Video, label: 'Telemedizin', order: 130 },
  },
  {
    id: 'warteliste',
    path: '/warteliste',
    component: Warteliste,
    featureFlag: 'waitlist',
    feature: 'warteliste',
    errorContext: 'Warteliste',
    scope: 'praxis',
    roles: [...WORKFLOW_ROLES],
    nav: { icon: Clock, label: 'Warteliste', order: 140 },
  },
  {
    id: 'gruppen',
    path: '/gruppen',
    component: Gruppen,
    featureFlag: 'group_therapy',
    feature: 'gruppen',
    errorContext: 'Gruppentherapie',
    scope: 'praxis',
    roles: [...WORKFLOW_ROLES],
    nav: { icon: UsersRound, label: 'Gruppentherapie', order: 150 },
  },
  {
    id: 'programme',
    path: '/programme',
    component: ProgrammePage,
    featureFlag: 'programs',
    errorContext: 'Programme',
    scope: 'praxis',
    roles: [...WORKFLOW_ROLES],
    nav: { icon: ClipboardList, label: 'Programme', order: 155 },
  },
  {
    id: 'uebergaben',
    path: '/uebergaben',
    component: UebergabenPage,
    featureFlag: 'clinical_consults',
    errorContext: 'Übergaben aus der Klinik',
    scope: 'praxis',
    roles: [...WORKFLOW_ROLES],
    nav: { icon: Inbox, label: 'Klinik-Übergaben', order: 156 },
  },
  {
    id: 'therapeuten',
    path: '/therapeuten',
    component: TherapeutenUebersicht,
    featureFlag: 'multi_org_admin',
    feature: 'multi_therapeuten',
    errorContext: 'Therapeuten-Übersicht',
    scope: 'praxis',
    roles: [...WORKFLOW_ROLES],
    nav: { icon: UserCheck, label: 'Therapeuten', order: 160 },
  },

  // ── Flow-Redesign (Beta) ──────────────────────────────────────────────
  //
  // Parallel, task-focused UI gated behind the `ui_flow_mode` add-on.
  // No sidebar `nav` entries (these screens replace the chrome entirely);
  // `FlowLayoutRouter` swaps in the full-screen Flow layouts. When the
  // flag is off the routes degrade to the standard FeatureGate notice.
  {
    id: 'flow-heute',
    path: '/flow',
    component: HeuteScreen,
    feature: 'ui_flow_mode',
    errorContext: 'Flow · Heute',
    roles: [...WORKFLOW_ROLES],
  },
  {
    id: 'flow-consultation',
    path: '/flow/consultation/:patientId',
    component: ConsultationScreen,
    feature: 'ui_flow_mode',
    errorContext: 'Flow · Konsultation',
    roles: [...WORKFLOW_ROLES],
  },
  {
    id: 'flow-review',
    path: '/flow/review/:patientId/:sessionId',
    component: ReviewScreen,
    feature: 'ui_flow_mode',
    errorContext: 'Flow · Review',
    roles: [...WORKFLOW_ROLES],
  },

  // ── Magic AI Importer (Phase 2) ───────────────────────────────────────
  //
  // Org-admin-only: data ingestion from competitor SaaS exports
  // (Nutrium, Doclib, …) routes through a multi-stage AI pipeline
  // before landing in fachliche Tabellen — that workflow is a
  // privileged operation, so therapists / assistance roles cannot
  // open it. The route is intentionally NOT gated behind a `feature`
  // flag in this PR because the `magic_importer` feature key has
  // not been added to `useFeatures.constants.ts` yet (out of scope
  // for the Phase-2 UI track). The next PR (Track G) will register
  // the feature key and tighten the gate here.
  {
    id: 'magic-importer',
    path: '/magic-importer',
    component: MagicImporter,
    featureFlag: 'magic_importer',
    errorContext: 'Magic Importer',
    roles: ['org_admin'],
    nav: {
      icon: Sparkles,
      label: 'Magic Importer',
      order: 95,
    },
  },
  {
    id: 'magic-importer-review',
    path: '/magic-importer/:jobId',
    component: MagicImporterReview,
    featureFlag: 'magic_importer',
    errorContext: 'Magic Importer Review',
    roles: ['org_admin'],
  },

  // ── Phase 5: Module Browser ───────────────────────────────────────────
  {
    id: 'modules',
    path: '/modules',
    component: ModuleBrowser,
    errorContext: 'Module-Browser',
    roles: [...WORKFLOW_ROLES],
    nav: {
      icon: LayoutGrid,
      label: 'Module',
      order: 995,
      section: 'admin',
    },
  },

  // ── Settings + admin ──────────────────────────────────────────────────
  {
    id: 'settings',
    path: '/settings',
    component: Settings,
    errorContext: 'Einstellungen',
    roles: [...WORKFLOW_ROLES],
    nav: { icon: SettingsIcon, label: 'Einstellungen', order: 1000, section: 'admin' },
  },
  {
    id: 'praxis-admin',
    path: '/praxis-admin',
    component: PraxisAdmin,
    featureFlag: 'multi_org_admin',
    errorContext: 'Praxis-Administration',
    roles: ['org_admin'],
    scope: 'praxis',
    nav: {
      label: 'Praxis-Admin',
      icon: Building2,
      order: 940,
      section: 'admin',
    },
  },
  {
    id: 'admin',
    path: '/admin',
    component: Kundenverwaltung,
    featureFlag: 'multi_org_admin',
    // Sidebar-Label: "Organisationen" (UX-Audit Phase 1.6). Der bisherige
    // Begriff "Kundenverwaltung" hat in einer Healthcare-Anwendung mit
    // Patient:innen-Daten einen falschen Klang ("Kunden" steht im Konflikt
    // zum DSGVO-Kontext) und kollidiert begrifflich mit der Patient:innen-
    // Liste. Der Dateiname `Kundenverwaltung.tsx` und der Route-Pfad
    // bleiben bewusst stabil, um bestehende Bookmarks und Imports nicht
    // zu brechen — nur das UI-Label wird umbenannt.
    errorContext: 'Organisationen',
    roles: ['admin'],
    nav: {
      icon: Shield,
      label: 'Organisationen',
      order: 1010,
      section: 'admin',
    },
  },
  {
    id: 'admin-pricing',
    path: '/admin/pricing',
    component: PricingManager,
    featureFlag: 'multi_org_admin',
    errorContext: 'Preisverwaltung',
    roles: ['admin'],
    nav: {
      icon: Receipt,
      label: 'Preisverwaltung',
      order: 1020,
      section: 'admin',
    },
  },
  {
    id: 'clinic-admin',
    path: '/clinic-admin',
    component: ClinicAdmin,
    featureFlag: 'multi_org_admin',
    errorContext: 'Klinik-Administration',
    roles: ['org_admin'],
    scope: 'klinik',
    nav: {
      label: 'Klinik-Admin',
      icon: Building2,
      order: 950,
      section: 'admin',
    },
  },
  {
    id: 'admin-bls-import',
    path: '/admin/bls-import',
    component: BlsImport,
    featureFlag: 'multi_org_admin',
    errorContext: 'BLS-Import',
    roles: ['admin'],
    nav: {
      icon: Database,
      label: 'BLS-Import',
      order: 1005,
      section: 'admin',
    },
  },
  {
    id: 'admin-leitlinien-import',
    path: '/admin/leitlinien-import',
    component: LeitlinienImport,
    featureFlag: 'multi_org_admin',
    errorContext: 'Leitlinien-Import',
    roles: ['admin'],
    nav: {
      icon: BookOpen,
      label: 'Leitlinien-Import',
      order: 1006,
      section: 'admin',
    },
  },
  {
    id: 'admin-audit-log',
    path: '/admin/audit-log',
    component: AuditLog,
    // F-13-02 (audit): audit log is a DSGVO Art. 30 surface that must
    // remain reachable irrespective of the MVP multi_org_admin flag.
    // RLS + canViewAuditLog widen the access to org admins safely.
    errorContext: 'Audit-Log',
    roles: ['admin', 'org_admin'],
    nav: {
      icon: ClipboardList,
      label: 'Audit-Log',
      order: 1030,
      section: 'admin',
    },
  },
  {
    id: 'admin-breach-report',
    path: '/admin/breach-report',
    component: BreachReport,
    // F-13-02 (audit): Art. 33 breach reporting must stay accessible
    // for the org admin (72h notification clock).
    errorContext: 'Datenpannen-Meldung',
    roles: ['admin', 'org_admin'],
    nav: {
      icon: Shield,
      label: 'Datenpannen',
      order: 1040,
      section: 'admin',
    },
  },
];

/**
 * Static legacy redirects: `/documents` was removed but old bookmarks
 * should still land on a sensible page. Keep these declarative so adding
 * one is just one line.
 */
export const REDIRECTS: ReadonlyArray<{ from: string; to: string }> = [
  { from: '/documents', to: '/patients' },
  // Phase 3: `/posteingang` (alter generischer Posteingang-Pfad) landet
  // jetzt auf der unified Review-Inbox (`/inbox`, Triage-Cockpit).
  // Hinweis: `/ki-posteingang` ist KEIN Redirect — es ist die
  // eigenständige Capture-Seite (Freitext/Datei → KI-Routing) und bleibt
  // als separate Route bestehen (siehe Route `ki-posteingang` oben).
  { from: '/posteingang', to: '/inbox' },
];
